/* Canvello — Saved pieces and Recently viewed (Task 7.5).
 *
 * Replaces Swym Wishlist Plus with no app and no third-party script:
 *   - any [data-cv-save="<handle>"] button toggles the piece in the browser's
 *     list (localStorage "cv:saved", newest first); aria-pressed carries the
 *     state, the button's name stays "Save {title}";
 *   - [data-cv-saved-count] shows how many (header, drawer);
 *   - a product page's [data-cv-recent="<handle>"] records the visit
 *     (localStorage "cv:recent", 12 kept);
 *   - CVSaved.cards(handles) fetches each piece's card through the Section
 *     Rendering API (sections/cv-card-fetch.liquid, in the visitor's
 *     language and currency) for the Saved page and the Recently viewed row.
 *
 * Lists live in this browser only (Swym's free plan was the same for guests);
 * the Saved page shares a list by link (?items=a,b,c).
 * Every storage call is guarded: private windows and blocked storage just
 * show nothing saved.
 */
(() => {
  if (window.CVSaved) return;

  const SAVED = 'cv:saved';
  const RECENT = 'cv:recent';
  const MAX_SAVED = 60;
  const MAX_RECENT = 12;
  const T = window.CVSavedStrings || {};
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const HANDLE = /^[a-z0-9][a-z0-9-]{0,254}$/;

  const read = (key) => {
    try {
      const v = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(v) ? v.filter((h) => typeof h === 'string' && HANDLE.test(h)) : [];
    } catch (e) {
      return [];
    }
  };
  const write = (key, list) => {
    try {
      localStorage.setItem(key, JSON.stringify(list));
    } catch (e) {
      /* storage blocked: the list lasts for this page only */
    }
  };

  let live = null;
  const say = (text) => {
    if (!text) return;
    if (!live) {
      live = document.createElement('p');
      live.className = 'visually-hidden';
      live.setAttribute('role', 'status');
      document.body.appendChild(live);
    }
    live.textContent = '';
    requestAnimationFrame(() => {
      live.textContent = text;
    });
  };

  const paint = (scope) => {
    const list = read(SAVED);
    const n = list.length;
    document.querySelectorAll('[data-cv-saved-count]').forEach((el) => {
      el.textContent = n > 99 ? '99+' : String(n);
      el.hidden = n === 0;
    });
    (scope || document).querySelectorAll('[data-cv-save]').forEach((btn) => {
      const on = list.indexOf(btn.dataset.cvSave) >= 0;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      const label = btn.querySelector('[data-cv-save-label]');
      if (label) label.textContent = on ? T.saved || 'Saved' : T.save || 'Save';
      if (btn.hasAttribute('data-cur')) btn.setAttribute('data-cur', on ? T.cur_saved || 'Saved' : T.cur_save || 'Save');
    });
  };

  // Every save is also told (2026-10-17): the Canvello API (counts per piece;
  // a signed-in customer's list on their record), Google Analytics'
  // add_to_wishlist (both GTM containers and gtag), and Shopify's customer
  // events ("cv_saved", for custom pixels). Fire and forget.
  const visitor = () => {
    try {
      let v = localStorage.getItem('cv:vid');
      if (!v) {
        v = Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem('cv:vid', v);
      }
      return v;
    } catch (e) {
      return '';
    }
  };
  const tell = (handle, on) => {
    const item = { item_id: handle, item_name: handle };
    try {
      if (on) {
        (window.dataLayer = window.dataLayer || []).push({ event: 'add_to_wishlist', ecommerce: { items: [item] } });
        if (typeof window.gtag === 'function') window.gtag('event', 'add_to_wishlist', { items: [item] });
      }
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('cv_saved', { handle, action: on ? 'add' : 'remove' });
      }
    } catch (e) {
      /* analytics never stops a save */
    }
    if (!T.api) return;
    try {
      fetch(`${T.api.replace(/\/+$/, '')}/saved`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handle, action: on ? 'add' : 'remove', visitor: visitor(), sig: T.sig || '', list: T.sig ? read(SAVED) : undefined }),
        keepalive: true,
      }).catch(() => {});
    } catch (e) {
      /* offline: the browser's list stands */
    }
  };

  const set = (list) => {
    write(SAVED, list.slice(0, MAX_SAVED));
    paint();
    document.dispatchEvent(new CustomEvent('cv:saved', { detail: { list: read(SAVED) } }));
  };

  const api = {
    list: () => read(SAVED),
    recent: () => read(RECENT),
    has: (h) => read(SAVED).indexOf(h) >= 0,
    toggle(h) {
      if (!HANDLE.test(h)) return false;
      const list = read(SAVED);
      const at = list.indexOf(h);
      if (at >= 0) list.splice(at, 1);
      else list.unshift(h);
      set(list);
      tell(h, at < 0);
      return at < 0;
    },
    add(handles) {
      const list = read(SAVED);
      handles
        .filter((h) => HANDLE.test(h))
        .reverse()
        .forEach((h) => {
          if (list.indexOf(h) < 0) {
            list.unshift(h);
            tell(h, true);
          }
        });
      set(list);
    },
    remove(h) {
      set(read(SAVED).filter((x) => x !== h));
      tell(h, false);
    },
    clear() {
      set([]);
    },
    paint,
    /* One card per handle, in order; pieces that no longer exist drop out. */
    cards(handles) {
      return Promise.all(
        handles.map((h) =>
          fetch(`${root}products/${encodeURIComponent(h)}?section_id=cv-card-fetch`, { credentials: 'same-origin' })
            .then((r) => (r.ok ? r.text() : ''))
            .then((html) => {
              if (!html) return null;
              const doc = new DOMParser().parseFromString(html, 'text/html');
              const card = doc.querySelector('[data-card]');
              return card ? { handle: h, card, variant: card.getAttribute('data-variant') } : null;
            })
            .catch(() => null)
        )
      ).then((all) => all.filter(Boolean));
    },
  };
  window.CVSaved = api;

  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-cv-save]');
    if (!btn) return;
    e.preventDefault();
    const on = api.toggle(btn.dataset.cvSave);
    btn.removeAttribute('data-pop');
    if (on) {
      void btn.offsetWidth;
      btn.setAttribute('data-pop', '');
    }
    say(on ? T.added || 'Saved' : T.removed || 'Removed from saved');
  });

  /* Another tab changed the list. */
  window.addEventListener('storage', (e) => {
    if (e.key === SAVED) paint();
  });

  /* Cards that arrive later (quick view, related rows, the bag) and the
   * drawer's count (built on first open) get their state. */
  const WATCH = '[data-cv-save], [data-cv-saved-count]';
  if ('MutationObserver' in window) {
    let queued = false;
    new MutationObserver((records) => {
      if (queued) return;
      for (const r of records) {
        for (const n of r.addedNodes) {
          if (n.nodeType === 1 && (n.matches(WATCH) || n.querySelector(WATCH))) {
            queued = true;
            requestAnimationFrame(() => {
              queued = false;
              paint();
            });
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* Recently viewed: the product page names its handle. */
  const record = () => {
    if (T.recent === false) return;
    const el = document.querySelector('[data-cv-recent]');
    const h = el && el.getAttribute('data-cv-recent');
    if (!h || !HANDLE.test(h)) return;
    const list = read(RECENT).filter((x) => x !== h);
    list.unshift(h);
    write(RECENT, list.slice(0, MAX_RECENT));
  };

  const start = () => {
    paint();
    record();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
